import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Check, ChevronLeft, ChevronRight, Code, Database, FileBarChart, Loader2, PanelLeftOpen, Pin, Sparkles, Zap, Clock
} from 'lucide-react';
import type { ChatMessage, ResponseBlock } from '../types';
import { TableBlock } from '../blocks/TableBlock';
import { ChartBlock } from '../blocks/ChartBlock';
import { KpiBlock } from '../blocks/KpiBlock';
import { SqlBlock } from '../blocks/SqlBlock';
import { FileBlock } from '../blocks/FileBlock';
import { FunnelBlock } from '../blocks/FunnelBlock';
import { HeatmapBlock } from '../blocks/HeatmapBlock';
import { TreemapBlock } from '../blocks/TreemapBlock';
import { type VizType } from '../blocks/VizSwitcher';
import { tableDataToChartData, chartDataToTableData, suggestVizType, type VizSuggestion } from '../../../../../utils/chartUtils';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ViewRenderer } from '../view/ViewRenderer';
import { ViewAdjustBar } from '../view/ViewAdjustBar';
import { useViewHistory } from '../view/useViewHistory';
import { chartConfigForSave } from '../view/legacy';
import { queuePin } from '../view/pendingPins';
import type { DatasetMap, ViewSpec } from '../view/types';
import { resultBlocks, resultSql } from './resultUtils';
import { ResultEmptyState } from './ResultEmptyState';

/** Chat-graftyper som dashboard-widgeten kan rendre, normalisert til widgetens sett (eldre svar uten visning). */
const WIDGET_CHART_TYPES: Record<string, string> = {
    bar: 'bar',
    line: 'line',
    pie: 'pie',
    area: 'area',
    'horizontal-bar': 'bar',
    'stacked-bar': 'bar',
};

interface ResultPanelProps {
    /** Svaret som vises (null = ingen resultat ennå) */
    message: ChatMessage | null;
    /** Spørsmålet som ga svaret */
    userPrompt: string;
    /** Compass jobber med et nytt svar */
    loading: boolean;
    /** Plassering blant resultatene i samtalen (1-basert) */
    position: number;
    count: number;
    onPrevious: () => void;
    onNext: () => void;
    onSaveAsReport?: (sql: string, prompt: string, exportFormat?: string, chartConfig?: string) => void;
    /** Oppfølgingsspørsmål (drill-down eller justering som krever nye data) */
    onAsk: (question: string) => void;
    /** Visningen ble endret — lagres på meldingen så den overlever bytte mellom resultater og lagres i samtalen */
    onViewChange: (messageId: string, view: ViewSpec, datasets: DatasetMap, originalView: ViewSpec) => void;
    /** Samtalen er skjult — vis knapp for å hente den fram */
    chatHidden?: boolean;
    onShowChat?: () => void;
    /** Vis ms/tokens/cache (bare for utviklere) */
    showTechnical?: boolean;
    /** Smal/lav skjerm: hode, resultat og justering ruller som én side i stedet for fast hode og bunn */
    singleScroll?: boolean;
}

/**
 * Høyre kolonne i Spør Compass: resultatet av valgt svar i full bredde, med én verktøylinje (lagre, pin, SQL)
 * og justering nederst. Samtalen står i venstre kolonne.
 */
export const ResultPanel: React.FC<ResultPanelProps> = (props) => {
    const { t } = useTranslation();
    const { message, loading, chatHidden, onShowChat } = props;

    const showChatButton = chatHidden && onShowChat ? (
        <button
            onClick={onShowChat}
            className="flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex-shrink-0"
        >
            <PanelLeftOpen size={16} />
            {t('manager.modules.claudeQuery.workspace.showChat', {}, 'Vis samtalen')}
        </button>
    ) : null;

    if (!message) {
        return (
            <div className="h-full flex flex-col">
                {showChatButton && <div className="p-3 border-b border-gray-100">{showChatButton}</div>}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {loading ? <ResultLoading /> : <ResultEmptyState onAsk={props.onAsk} />}
                </div>
            </div>
        );
    }

    // Nøklet på meldingen: angre/gjør om gjelder ett resultat, og byttes når et annet resultat velges
    return <ResultBody key={message.id} {...props} message={message} showChatButton={showChatButton} />;
};

/** Fremdrift mens Compass jobber — ærlig om at det tar litt tid, og hva som skjer. */
const ResultLoading: React.FC = () => {
    const { t } = useTranslation();
    const steps = [
        t('manager.modules.claudeQuery.workspace.stepUnderstand', {}, 'Tolker spørsmålet'),
        t('manager.modules.claudeQuery.workspace.stepData', {}, 'Henter data fra lageret'),
        t('manager.modules.claudeQuery.workspace.stepView', {}, 'Setter sammen visningen'),
    ];
    const [step, setStep] = useState(0);
    useEffect(() => {
        const timer = window.setInterval(() => setStep(s => Math.min(s + 1, steps.length - 1)), 4000);
        return () => window.clearInterval(timer);
    }, [steps.length]);

    return (
        <div className="p-6" aria-live="polite">
            <div className="flex items-center gap-2 mb-1">
                <Loader2 size={18} className="animate-spin text-blue-600" />
                <span className="text-sm font-medium text-gray-700">{steps[step]}…</span>
            </div>
            <p className="text-xs text-gray-500 mb-6">
                {t('manager.modules.claudeQuery.workspace.loadingHint', {}, 'Det tar vanligvis 10–20 sekunder.')}
            </p>
            {/* Skjelett i samme form som en typisk visning: nøkkeltall, to grafer, tabell */}
            <div className="space-y-3 animate-pulse">
                <div className="grid grid-cols-4 gap-3">
                    {[0, 1, 2, 3].map(i => <div key={i} className="h-20 rounded-lg bg-gray-100" />)}
                </div>
                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-7 h-56 rounded-lg bg-gray-100" />
                    <div className="col-span-5 h-56 rounded-lg bg-gray-100" />
                </div>
                <div className="h-40 rounded-lg bg-gray-100" />
            </div>
        </div>
    );
};

const ResultBody: React.FC<ResultPanelProps & { message: ChatMessage; showChatButton: React.ReactNode }> = ({
    message, userPrompt, loading, position, count, onPrevious, onNext, onSaveAsReport, onAsk, onViewChange, showChatButton, showTechnical,
    singleScroll
}) => {
    const { t } = useTranslation();
    const history = useViewHistory(message.view, message.originalView);
    const viewState = history.view;
    const [datasetsState, setDatasetsState] = useState<DatasetMap | undefined>(message.datasets);
    const hasView = !!viewState && !!datasetsState;
    const datasetSql = useMemo(() => Object.fromEntries(Object.values(datasetsState ?? {})
        .filter(d => !!d.sql).map(d => [d.id, d.sql as string])), [datasetsState]);
    const [showSql, setShowSql] = useState(false);
    const [isPinning, setIsPinning] = useState(false);
    const [pinSuccess, setPinSuccess] = useState(false);

    // Endret visning lagres på meldingen (samtalen lagres, og versjonen beholdes ved bytte av resultat)
    useEffect(() => {
        if (viewState && datasetsState && (viewState !== message.view || datasetsState !== message.datasets))
            onViewChange(message.id, viewState, datasetsState, message.originalView ?? message.view ?? viewState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewState, datasetsState]);

    /** Presentasjonen som lagres på rapporten: visningen (+ legacy-felter), ellers graf-oppsettet uten data. */
    const deriveReportPresentation = useCallback((): { exportFormat: string; chartConfig?: string } => {
        if (viewState) return chartConfigForSave(viewState);
        const cd = message.blocks?.find(b => b.type === 'chart' && b.chartData)?.chartData;
        if (cd) {
            const normalized = WIDGET_CHART_TYPES[(cd.chartType || '').toLowerCase()];
            if (normalized) {
                const chartConfig = JSON.stringify({
                    chartType: normalized,
                    title: cd.title,
                    xAxis: cd.xAxis,
                    yAxis: cd.yAxis,
                    series: cd.series?.map(s => ({ name: s.name, dataKey: s.dataKey, color: s.color })),
                });
                return { exportFormat: 'chart', chartConfig };
            }
        }
        return { exportFormat: 'table' };
    }, [message.blocks, viewState]);

    const reportName = (viewState?.title || userPrompt || '').slice(0, 60)
        || t('manager.modules.claudeQuery.messageBubble.compassReport', {}, 'Compass-rapport');

    const handlePinToDashboard = useCallback(async () => {
        if (!message.generatedSQL || isPinning) return;
        setIsPinning(true);
        try {
            const { claudeQueryService } = await import('../../../../../services/claudeQueryService');
            const presentation = deriveReportPresentation();
            const report = await claudeQueryService.createReport({
                name: reportName,
                description: userPrompt && userPrompt !== reportName ? userPrompt : undefined,
                generatedSQL: message.generatedSQL,
                originalPrompt: userPrompt || '',
                exportFormat: presentation.exportFormat,
                chartConfig: presentation.chartConfig,
                category: 'Dashboard',
            });

            let displayMode = viewState ? 'view' : 'table';
            const firstChartBlock = message.blocks?.find(b => b.type === 'chart');
            if (!viewState && firstChartBlock?.chartData)
                displayMode = WIDGET_CHART_TYPES[(firstChartBlock.chartData.chartType || '').toLowerCase()] || 'bar';

            // Køen hentes av dashboardet også når det ikke er åpent nå
            queuePin({ reportId: report.reportId, displayMode, title: reportName });
            setPinSuccess(true);
            setTimeout(() => setPinSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to pin to dashboard:', error);
        } finally {
            setIsPinning(false);
        }
    }, [message.generatedSQL, message.blocks, userPrompt, isPinning, deriveReportPresentation, viewState, reportName]);

    const sqlList = resultSql(message);
    const title = viewState?.title || userPrompt;
    const description = viewState?.description;

    return (
        <div className={singleScroll ? 'h-full overflow-y-auto' : 'h-full flex flex-col'}>
            {/* Hode: hva resultatet er, og navigering mellom resultatene i samtalen */}
            <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-start gap-3">
                    {showChatButton}
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-gray-800 truncate">{title}</h2>
                        {description && <p className="text-xs text-gray-500 truncate">{description}</p>}
                        {userPrompt && userPrompt !== title && (
                            <p className="text-xs text-gray-400 truncate">
                                {t('manager.modules.claudeQuery.workspace.answerTo', { '0': userPrompt }, `Svar på: «${userPrompt}»`)}
                            </p>
                        )}
                    </div>
                    {count > 1 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                                onClick={onPrevious}
                                disabled={position <= 1}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                aria-label={t('manager.modules.claudeQuery.workspace.previousResult', {}, 'Forrige resultat')}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <span className="text-xs text-gray-500 px-1 whitespace-nowrap">
                                {t('manager.modules.claudeQuery.workspace.resultOf', { '0': position, '1': count }, `${position} av ${count}`)}
                            </span>
                            <button
                                onClick={onNext}
                                disabled={position >= count}
                                className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                aria-label={t('manager.modules.claudeQuery.workspace.nextResult', {}, 'Neste resultat')}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Verktøylinje: det som gjør svaret til varig verdi står først */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {message.generatedSQL && onSaveAsReport && (
                        <button
                            onClick={() => {
                                const p = deriveReportPresentation();
                                onSaveAsReport(message.generatedSQL!, userPrompt || '', p.exportFormat, p.chartConfig);
                            }}
                            className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                        >
                            <FileBarChart size={16} />
                            {t('manager.modules.claudeQuery.messageBubble.saveAsReport', {}, 'Lagre som rapport')}
                        </button>
                    )}
                    {message.generatedSQL && (
                        <button
                            onClick={handlePinToDashboard}
                            disabled={isPinning}
                            className={`flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg border text-sm font-medium transition-colors ${pinSuccess
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        >
                            {pinSuccess ? <Check size={16} /> : isPinning ? <Loader2 size={16} className="animate-spin" /> : <Pin size={16} />}
                            {pinSuccess
                                ? t('manager.modules.claudeQuery.workspace.pinnedHint', {}, 'Lagt på dashboardet')
                                : t('manager.modules.claudeQuery.messageBubble.pinToDashboard', {}, 'Pin til dashboard')}
                        </button>
                    )}
                    {sqlList.length > 0 && (
                        <button
                            onClick={() => setShowSql(s => !s)}
                            aria-pressed={showSql}
                            className={`flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-sm transition-colors ${showSql
                                ? 'bg-gray-100 text-gray-800'
                                : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            <Code size={16} />
                            {t('manager.modules.claudeQuery.workspace.sql', {}, 'SQL')}
                        </button>
                    )}
                    {showTechnical && (
                        <span className="ml-auto flex items-center gap-3 text-xs text-gray-400">
                            {message.executionTimeMs !== undefined && <span className="flex items-center gap-1"><Clock size={11} />{message.executionTimeMs} ms</span>}
                            {message.tokensUsed !== undefined && <span className="flex items-center gap-1"><Zap size={11} />{message.tokensUsed} tokens</span>}
                            {message.cacheHit && <span className="flex items-center gap-1"><Database size={11} />cache</span>}
                        </span>
                    )}
                </div>
                {message.snapshot && (
                    <p className="text-xs text-gray-400 mt-1">
                        {t('manager.modules.claudeQuery.view.snapshotNote', {}, 'Øyeblikksbilde fra lagret samtale. Lagre som rapport for å hente ferske data.')}
                    </p>
                )}
            </div>

            {/* Selve resultatet */}
            <div className={singleScroll ? 'p-4' : 'flex-1 min-h-0 overflow-y-auto p-4'}>
                {loading && (
                    <div className="mb-3 flex items-center gap-2 text-xs text-gray-500" aria-live="polite">
                        <Loader2 size={14} className="animate-spin text-blue-600" />
                        {t('manager.modules.claudeQuery.workspace.newResultComing', {}, 'Compass lager et nytt resultat …')}
                    </div>
                )}
                {showSql && (
                    <div className="mb-3">
                        {sqlList.map(s => (
                            <SqlBlock
                                key={s.id}
                                sql={s.sql}
                                defaultExpanded
                                label={sqlList.length > 1 ? t('manager.modules.claudeQuery.workspace.sqlFor', { '0': s.id }, `SQL for ${s.id}`) : undefined}
                            />
                        ))}
                    </div>
                )}
                {hasView
                    ? <ViewRenderer view={viewState!} datasets={datasetsState!} onViewChange={history.push} compact />
                    : <LegacyResult message={message} onAsk={onAsk} />}
            </div>

            {/* Justering nederst, som chat-feltet i venstre kolonne */}
            {hasView && (
                <div className="px-4 pb-3 border-t border-gray-100 flex-shrink-0">
                    <ViewAdjustBar
                        view={viewState!}
                        datasets={datasetsState!}
                        datasetSql={datasetSql}
                        onAdjusted={(v, ds) => { history.push(v); if (ds) setDatasetsState(prev => ({ ...prev, ...ds })); }}
                        onNeedsDataChange={(instruction) => onAsk(instruction)}
                        canUndo={history.canUndo}
                        canRedo={history.canRedo}
                        isModified={history.isModified}
                        onUndo={history.undo}
                        onRedo={history.redo}
                        onReset={history.reset}
                    />
                </div>
            )}
        </div>
    );
};

/** Datablokker fra eldre svar (uten Compass-visning): graf/tabell med graftypebytte og forslag. */
const LegacyResult: React.FC<{ message: ChatMessage; onAsk: (q: string) => void }> = ({ message, onAsk }) => {
    const { t } = useTranslation();
    const [vizOverrides, setVizOverrides] = useState<Record<number, VizType>>({});
    const [dismissed, setDismissed] = useState<Record<number, boolean>>({});

    const suggestions = useMemo(() => {
        const result: Record<number, VizSuggestion> = {};
        (message.blocks ?? []).forEach((block, idx) => {
            if (block.type === 'chart' && block.chartData) {
                const flat = block.chartData.series?.[0]?.data || [];
                if (flat.length > 0) {
                    const s = suggestVizType(flat, undefined, block.chartData.chartType);
                    if (s.confidence !== 'low' && s.suggestedType !== block.chartData.chartType?.toLowerCase()) result[idx] = s;
                }
            } else if (block.type === 'table' && block.tableData) {
                const s = suggestVizType(block.tableData.rows, block.tableData.columns, 'table');
                if (s.confidence !== 'low' && s.suggestedType !== 'table') result[idx] = s;
            }
        });
        return result;
    }, [message.blocks]);

    const switchViz = (idx: number, type: VizType) => {
        setVizOverrides(prev => ({ ...prev, [idx]: type }));
        setDismissed(prev => ({ ...prev, [idx]: true }));
    };
    const drill = (template: string, fallback: string, value: string) =>
        onAsk(t(template, { '0': value }, fallback));

    const badge = (idx: number) => {
        const s = suggestions[idx];
        if (!s || dismissed[idx] || vizOverrides[idx]) return null;
        const labels: Record<string, string> = {
            kpi: t('manager.modules.claudeQuery.messageBubble.vizKpiCard', {}, 'KPI-kort'),
            bar: t('manager.modules.claudeQuery.messageBubble.vizBarChart', {}, 'Stolpediagram'),
            line: t('manager.modules.claudeQuery.messageBubble.vizLineChart', {}, 'Linjediagram'),
            area: t('manager.modules.claudeQuery.messageBubble.vizAreaChart', {}, 'Omradediagram'),
            pie: t('manager.modules.claudeQuery.messageBubble.vizPieChart', {}, 'Kakediagram'),
            table: t('manager.modules.claudeQuery.messageBubble.vizTable', {}, 'Tabell'),
        };
        const label = labels[s.suggestedType] || s.suggestedType;
        return (
            <div className="flex items-center gap-2 mt-1">
                <button
                    onClick={() => switchViz(idx, s.suggestedType as VizType)}
                    className="flex items-center gap-1 min-h-[44px] px-3 text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 transition-colors"
                >
                    <Sparkles size={12} />
                    {t('manager.modules.claudeQuery.messageBubble.suggestionPrefix', { '0': label }, `Forslag: ${label}`)}
                </button>
                <button onClick={() => setDismissed(prev => ({ ...prev, [idx]: true }))} className="min-h-[44px] px-2 text-xs text-gray-400 hover:text-gray-600">
                    {t('manager.modules.claudeQuery.messageBubble.dismiss', {}, 'Avvis')}
                </button>
            </div>
        );
    };

    const render = (block: ResponseBlock, idx: number) => {
        const override = vizOverrides[idx];
        switch (block.type) {
            case 'table':
                if (!block.tableData) return null;
                if (override && override !== 'table')
                    return <ChartBlock key={idx} data={tableDataToChartData(block.tableData, override) as any} currentVizType={override} onVizSwitch={(v) => switchViz(idx, v)} />;
                return (
                    <React.Fragment key={idx}>
                        <TableBlock data={block.tableData} generatedSQL={message.generatedSQL} onVizSwitch={(v) => switchViz(idx, v)} />
                        {badge(idx)}
                    </React.Fragment>
                );
            case 'chart':
                if (!block.chartData) return null;
                if (override === 'table')
                    return <TableBlock key={idx} data={chartDataToTableData(block.chartData as any) as any} generatedSQL={message.generatedSQL} onVizSwitch={(v) => switchViz(idx, v)} />;
                return (
                    <React.Fragment key={idx}>
                        <ChartBlock
                            data={block.chartData}
                            currentVizType={override || (block.chartData.chartType?.toLowerCase() as VizType)}
                            onVizSwitch={(v) => switchViz(idx, v)}
                            onDataPointClick={(label) => drill('manager.modules.claudeQuery.messageBubble.drillDownDetails', `Vis detaljer for "${label}"`, label)}
                        />
                        {badge(idx)}
                    </React.Fragment>
                );
            case 'kpi':
                return block.kpiData ? <KpiBlock key={idx} data={block.kpiData} /> : null;
            case 'file':
                return block.fileData ? <FileBlock key={idx} data={block.fileData} /> : null;
            case 'funnel':
                return block.funnelData ? (
                    <FunnelBlock key={idx} data={block.funnelData}
                        onStepClick={(step) => drill('manager.modules.claudeQuery.messageBubble.drillDownOrderStatus', `Vis detaljer for ordrer med status "${step.label}"`, step.label)} />
                ) : null;
            case 'heatmap':
                return block.heatmapData ? (
                    <HeatmapBlock key={idx} data={block.heatmapData}
                        onCellClick={(cell) => drill('manager.modules.claudeQuery.messageBubble.drillDownLocation', `Vis detaljer for lokasjon ${cell.code}`, cell.code)} />
                ) : null;
            case 'treemap':
                return block.treemapData ? (
                    <TreemapBlock key={idx} data={block.treemapData}
                        onNodeClick={(node) => drill('manager.modules.claudeQuery.messageBubble.drillDownCategory', `Vis detaljer for kategori "${node.name}"`, node.name)} />
                ) : null;
            default:
                return null;
        }
    };

    return <div className="space-y-3">{resultBlocks(message).map(({ block, index }) => render(block, index))}</div>;
};
